import {
    createContext,
    useState,
    useEffect,
    useContext,
} from "react";
import { useRouter } from "next/router";
import BusinessContext from "./business-context";
import { renderTables, OnCreateTable, renderRecentTables, addRecentTables, onCreateRow, renderRows, updateTable } from "../lib/table";
import { add, set } from "date-fns";
import { generateUniqueId } from "../lib/utils";

const TableContext = createContext({
    tables: [],
    setTables: () => { },
    createTable: () => { },
    fetchTables: () => { },
    newTableId: null,
    setNewTableId: () => { },
    refreshNewTableId: () => { },
    recentTables: [],
    setRecentTables: () => { },
    fetchRecentTables: () => { },
    updateRecentTables: () => { },
    duplicateTable: () => { },
    isTableDuplicating: false,
    setIsTableDuplicating: () => { },
    announcementTable: null,
    setAnnouncementTable: () => { },
    editSelectedProperty: null,
    setEditSelectedProperty: () => { },
    isShareModalOpen: false,
    toggleShareModal: () => {}
});

export const TableProvider = (props) => {
    const [tables, setTables] = useState(null);
    const [newTableId, setNewTableId] = useState(null);
    const [recentTables, setRecentTables] = useState(null);
    const router = useRouter();
    const { activeBusiness } = useContext(BusinessContext);
    const [isTableDuplicating, setIsTableDuplicating] = useState(false);
    const [announcementTable, setAnnouncementTable] = useState(null);
    const [editSelectedProperty, setEditSelectedProperty] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)

    const toggleShareModal = () => { setIsShareModalOpen(!isShareModalOpen)}
    const businessId = (() => {
        const path = router.asPath || router.pathname || "";
        const parts = path.split("?")[0].split("/");
        return parts[1] || null;
    })();

    const refreshNewTableId = () => {
        setNewTableId(null);
    }

    const setDefaultVisibleKanbanColumns = (table, callBack) => {
        const firstTextColumn = table.columns.find(column => column.type === 'text');
        const firstTeammateColumn = table.columns.find(column => column.type === 'teammate');

        const metadata = {
            ...table.metadata,
            extra: {
                ...table.metadata?.extra,
                visibleColumnsOnKanban:
                    [firstTextColumn ? firstTextColumn.id : null,
                    firstTeammateColumn ? firstTeammateColumn.id : null].filter(Boolean)
            }
        };

        const updatedTable = {
            ...table,
            metadata
        };

        const onError = (json) => {
            console.error(json);
        };

        updateTable(table.uuid, updatedTable, callBack, activeBusiness, onError, router)
    }

    const createTable = (type, folderName, additionalCallback, folderPermission, translations, query) => {
        const tableCreationCallback = (json) => {
            { additionalCallback && additionalCallback(json); }
            {
                type !== 'folder' && type !== 'announcement' &&
                    router.push(`/${businessId}/t/${json.uuid}${query ? `?${query}` : ''}`);
                setNewTableId(json.uuid);
            }
            fetchTables();
            fetchRecentTables();
        }

        const callback = (json) => {
            if (type === 'kanban') {
                setDefaultVisibleKanbanColumns(json, tableCreationCallback);
            }
            else {
                tableCreationCallback(json);
            }
        };

        const onError = (json) => {
            console.error(json);
        };
        let schema;
        const baseSchema = {
            "owner": 1,
            "business_permission": 4,
            "individual_permissions": [],
            "public_permission": 0
        };

        // Default translations (fallback to English)
        const t = translations || {
            columns: {
                task: "Task",
                status: "Status",
                assignedTo: "Assigned to",
                text: "Text",
                name: "Name",
                objectives: "Objectives",
                keyResults: "Key Results"
            },
            statusOptions: {
                toDo: "To Do",
                inProgress: "In Progress",
                done: "Done"
            },
            names: {
                kanban: "Kanban",
                untitledTable: "Untitled table",
                untitledDocument: "Untitled document",
                okrs: "OKRs",
                newFolder: "New Folder"
            }
        };

        if (type === 'kanban') {
            schema = {
                ...baseSchema,
                "name": t.names.kanban,
                "columns": [
                    {
                        "name": t.columns.task,
                        "type": "text",
                        "position": 1000.0,
                        "required": true
                    },
                    {
                        "name": t.columns.status,
                        "type": "mutable_select",
                        "options": {
                            "choices": [
                                { "ref": "to_do", "tag": t.statusOptions.toDo, "color": "#B84848" },
                                { "ref": "in_progress", "tag": t.statusOptions.inProgress, "color": "#8E6622" },
                                { "ref": "done", "tag": t.statusOptions.done, "color": "#3C821E" }
                            ]
                        },
                        "position": 2000
                    },
                    {
                        "name": t.columns.assignedTo,
                        "type": "teammate",
                        "position": 3000.0,
                    }
                ],
                "metadata": {
                    "render_types": [{
                        "type": "board",
                        "representation_column": t.columns.task,
                        "grouping_column": t.columns.status,
                        "is_default": true
                    }]
                }
            };
        } else if (type === 'blank') {
            schema = {
                ...baseSchema,
                "name": t.names.untitledTable,
                "columns": [{
                    "name": t.columns.text,
                    "type": "text",
                    "position": 1000.0,
                    "required": true
                }],
                "metadata": {}
            };
        } else if (type === 'document') {
            schema = {
                ...baseSchema,
                "name": t.names.untitledDocument,
                "columns": [{
                    "name": t.columns.name,
                    "type": "text",
                    "position": 1000.0,
                    "required": true
                }],
                "metadata": {
                    "render_types": [{
                        "type": "document",
                        "representation_column": "",
                        "grouping_column": "",
                        "is_default": true
                    }]
                }
            };
        } else if (type === 'okrs') {
            schema = {
                ...baseSchema,
                "name": t.names.okrs,
                "columns": [
                    {
                        "name": t.columns.objectives,
                        "type": "text",
                        "position": 1000.0,
                        "required": true
                    },
                    {
                        "name": t.columns.keyResults,
                        "type": "many_to_many",
                        "metadata": {
                            "datatype": "text"
                        },
                        "position": 2000.0
                    },
                ],
                "metadata": {}
            };
        }
        else if (type === 'folder') {
            schema = {
                ...baseSchema,
                "name": "",
                "type": "text",
                "columns": [],
                "metadata": {
                    "extra": {
                        "folderData": {
                            "id": generateUniqueId([], 12),
                            "is_folder": true,
                            "folder_name": folderName || t.names.newFolder,
                            "permission": folderPermission || null
                        }
                    }
                }

            }
        }
        else if (type === 'announcement') {
            schema = {
                ...baseSchema,
                "name": "Announcement Table",
                "namespace": "announcement",
                "columns": [
                    {
                        "name": "Announcement Ids",
                        "type": "many_to_many",
                        "metadata": {
                            "datatype": "text"
                        },
                        "position": 1000.0,
                    }
                ],
            };
        }
        OnCreateTable(schema, callback, activeBusiness, onError, router);
    }

    const fetchTables = (namespace) => {
        const callback = (json) => {
            setTables(json.filter(table => table?.namespace !== 'announcement'));
            setAnnouncementTable(json.find(table => table?.namespace === 'announcement'));
        }
        const onError = (json) => {
            console.error(json);
        }
        renderTables(callback, activeBusiness, onError, router, '@', namespace)
    }

    const fetchRecentTables = () => {
        const callback = (json) => {
            setRecentTables(json);
        }
        const onError = (json) => {
            console.error(json);
        }
        renderRecentTables(callback, activeBusiness, onError, router)
    }
    const updateRecentTables = (tableId, staffId) => {
        const onError = (json) => {
            console.error(json);
        }
        addRecentTables(tableId, staffId, activeBusiness, onError, router)
    }

    useEffect(() => {
        if (router.pathname.includes('/home')) {
            fetchRecentTables();
        }
        fetchTables();
    }, [activeBusiness, router]);

    const duplicateTable = (table) => {
        setIsTableDuplicating(true);
        const onError = (json) => {
            setIsTableDuplicating(false);
            console.error(json);
        };

        const tableDuplicate = {
            "name": `Duplicate of ${table.name}`,
            "owner": 1,
            "business_permission": 4,
            "individual_permissions": [],
            "public_permission": 0,
            "columns": table.columns,
            "metadata": table.metadata,
        };

        let newTableId;

        const onRowsFetched = (rows) => {
            rows?.forEach((row) => {
                const { id, permission, ...rowWithoutId } = row;
                onCreateRow(newTableId, null, rowWithoutId, activeBusiness, onError, router);
            });
            router.push(`/${businessId}/t/${newTableId}`);
        };

        const onTableCreated = (newTableJson) => {
            newTableId = newTableJson.uuid;
            renderRows(table.uuid, onRowsFetched, activeBusiness, onError, router
            );
        };

        OnCreateTable(tableDuplicate, onTableCreated, activeBusiness, onError, router);
    };



    const context = {
        tables,
        setTables,
        createTable,
        fetchTables,
        newTableId,
        setNewTableId,
        refreshNewTableId,
        recentTables,
        setRecentTables,
        fetchRecentTables,
        updateRecentTables,
        duplicateTable,
        isTableDuplicating,
        setIsTableDuplicating,
        announcementTable,
        setAnnouncementTable,
        editSelectedProperty,
        setEditSelectedProperty,
        isShareModalOpen,
        toggleShareModal
    };

    return (
        <TableContext.Provider value={context}>
            {props.children}
        </TableContext.Provider>
    );
};

export default TableContext;